import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { FaMapLocationDot, FaPhone, FaMap, FaList } from "react-icons/fa6";
import { IoIosMail } from "react-icons/io";
import { IoTime } from "react-icons/io5";

const BranchesContainer = styled.div`
  min-height: 100vh;
  background-color: #f8f9fa;
`;

const HeroSection = styled.section`
  position: relative;
  color: white;
  height: 50vh;
  padding: 80px 0;
  text-align: center;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: linear-gradient(135deg, #ff6720 0%, #ff8c42 100%); /* Desktop gradient */

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 60%;
    height: 100%;
    background: 
      url('https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/branchespagebanner.webp')
        center/cover no-repeat;
    clip-path: polygon(15% 0%, 100% 0%, 100% 100%, 0% 100%);
    z-index: 1;
  }

  /* Responsive: Remove gradient, show only image + text */
  @media (max-width: 768px) {
    padding: 60px 0;
    height: 25vh;
    background: transparent;
    background-image: url('https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/branchespagebanner.webp');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    &::before {
      display: none;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1;
    }

    div.container {
      text-align: center;
      width: 100%;
      padding: 0 20px;
      position: relative;
      z-index: 2;
    }
  }

  div.container {
    max-width: 1300px;
    border-radius: 10px;
    text-align: left;
    display: inline-block;
    margin: 0 auto;
    position: relative;
    z-index: 2;
  }
`;

const HeroTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 900;
  margin-bottom: 20px;
  line-height: 1.2;
  max-width: 500px;

  @media (max-width: 768px) {
    font-size: 2.5rem;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    font-size: 2rem;
  }
`;

const HeroSubtitle = styled.p`
  font-size: 1.3rem;
  max-width: 500px;
  opacity: 0.95;
  line-height: 1.5;
  margin-top: 10px;
  margin-bottom: 30px;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    max-width: 100%;
  }

  @media (max-width: 480px) {
    font-size: 1rem;
  }
`;

const BranchesSection = styled.section`
  padding: 80px 0;

  @media (max-width: 480px) {
    padding: 40px 0;
  }
`;

const FilterAndViewContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 50px;
  gap: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    width: 100%;
  }
`;

const LocationFilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: flex-end; /* Push to the right */
  order: 2; /* Add this to keep it on the right */

  @media (max-width: 768px) {
    width: 100%;
    justify-content: flex-start;
    order: 1; /* On mobile, show before view buttons */
  }
`;

const LocationFilterLabel = styled.span`
  font-size: 1rem;
  font-weight: 600;
  color: #333;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 0.95rem;
  }
`;

const LocationFilterSelect = styled.select`
  padding: 12px 40px 12px 20px;
  border: 1px solid #333;
  border-radius: 25px;
  background-color: white;
  color: #333;
  font-weight: 600;
  font-size: 1rem;
  appearance: none;
  background-image: url('data:image/svg+xml;utf8,<svg fill="%23333" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M7 10l5 5 5-5z"/></svg>');
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 20px;
  cursor: pointer;
  width: fit-content;
  transition: all 0.3s ease;

  &:hover {
    background-color: #ffffffff;
  }

  &:focus {
    outline: #333;
    border-color: #333;
    // box-shadow: 0 0 0 3px rgba(255, 103, 32, 0.1);
  }

  @media (max-width: 768px) {
    flex: 1;
    min-width: auto;
    font-size: 0.95rem;
    padding: 10px 35px 10px 16px;
  }
`;

const ViewToggleButtons = styled.div`
  display: flex;
  gap: 12px;
  background: white;
  padding: 6px;
  border-radius: 30px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  order: 1; /* Add this to keep it on the left */

  @media (max-width: 768px) {
    width: 100%;
    order: 2; /* On mobile, show after dropdown */
  }
`;

const ViewButton = styled.button`
  padding: 10px 24px;
  border: none;
  border-radius: 25px;
  background-color: ${(props) => (props.active ? "#ff6720" : "transparent")};
  color: ${(props) => (props.active ? "white" : "#666")};
  font-weight: 600;
  font-size: 0.95rem;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  &:hover {
    background-color: ${(props) => (props.active ? "#ff6720" : "#f5f5f5")};
    color: ${(props) => (props.active ? "white" : "#ff6720")};
  }

  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
    font-size: 0.9rem;
    padding: 10px 16px;
  }

  svg {
    background-color: ${(props) => (props.active ? "#ff6720" : "transparent")};
    fill: ${(props) => (props.active ? "white" : "#666")};
  }
`;

const BranchGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  max-width: 1300px;
  margin: 0 auto;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    padding: 0px;
    gap: 20px;
  }
`;

const BranchCard = styled.div`
  background: white;
  border-radius: 15px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  }
`;

const BranchImage = styled.div`
  height: 200px;
  background: ${props => props.$bgImage 
    ? `url(${props.$bgImage}) center/cover no-repeat` 
    : 'linear-gradient(135deg, #ff6720, #ff8c42)'};
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: ${props => props.$bgImage ? '""' : '"🏢"'};
    font-size: 4rem;
  }
`;

const BranchContent = styled.div`
  padding: 25px;
`;

const BranchName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 10px;
`;

const BranchType = styled.span`
  background-color: #ff6720;
  color: white;
  padding: 4px 12px;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: 600;
  margin-bottom: 15px;
  display: inline-block;
`;

const BranchInfo = styled.div`
  margin-bottom: 20px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 12px;
  font-size: 0.95rem;
  color: #666;
`;

const InfoIcon = styled.span`
  margin-right: 12px;
  font-size: 1.1rem;
  width: 20px;
`;

const BranchActions = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const ActionButton = styled.button`
  padding: 10px 20px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.3s ease;
  flex: 1;
  min-width: 120px;

  &.primary {
    background-color: #ff6720;
    color: white;

    &:hover {
      background-color: #e55a1c;
    }
  }

  &.secondary {
    background-color: transparent;
    color: #ff6720;
    border: 1px solid #ff6720;

    &:hover {
      background-color: #ff6720;
      color: white;
    }
  }
`;

const BnIcon = styled.div`
  font-size: 20px;
  display: flex;
  margin-right: 10px;
  color: #1b1b1bff;
  align-items: center;
  justify-content: center;
`;

const MapSection = styled.div`
  display: ${props => props.show ? 'block' : 'none'};
  padding: 0;
  background: white;
`;

const MapContainer = styled.div`
  max-width: 1300px;
  margin: 0 auto;
`;

const MapWrapper = styled.div`
  width: 100%;
  height: 600px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);

  #branches-map {
    width: 100%;
    height: 100%;
  }

  @media (max-width: 768px) {
    height: 500px;
    border-radius: 0;
  }

  @media (max-width: 480px) {
    height: 400px;
  }
`;

const BranchGridSection = styled.div`
  display: ${props => props.show ? 'block' : 'none'};
`;

const branches = [
  {
    id: 1,
    name: "STIHL XPLOD TOOLS",
    type: "Head Office",
    category: "pathanamthitta",
    address: "SH8, Kumbazha, Mylapra, Pathanamthitta, Kerala",
    mapUrl: "https://maps.app.goo.gl/d7fku4BCmeMAFwb5A",
    phone: "+91 9446189273",
    email: "info@blackanddecker.com",
    image: "https://lh3.googleusercontent.com/gps-cs-s/AC9h4noDFV9bFGfGYbrxI0AEbvyXsGd-PEYSSd1Mp2hDfj0tuFcFG6mHbbKDox7YS2I3I0r0ohfTApuqLLVEjAmR4UO5aU9aru6pbUuQipsOuvCN5ARqzZEyDSw1uvdNcM1f2eGsX_14SA=w408-h725-k-no",
    hours: "Mon-Sat: 8:30 AM - 7:30 PM",
    services: [
      "Corporate Operations",
      "Customer Service",
      "Product Development",
    ],
  },
  {
    id: 2,
    name: "STIHL AGRI TECH",
    type: "Branch",
    category: "trivandrum",
    address: "PVMX+W6Q, Kazhakoottam - Kilimanoor Rd, Karette, Kerala",
    lat: 8.734849322127499,
    lng: 76.89801195204323,
    phone: "+1 (212) 555-0123",
    email: "ny.service@blackanddecker.com",
    image: "https://lh3.googleusercontent.com/gps-cs-s/AC9h4nr5zA5qbgaN3g1-ed7hauqw89Xae9Cf6czktgSUeEsVFYksYzI71FkITsY9AYkZ3fQ_URbEO9GOky0xW89rWg9SL865zDT8ZesSTM-sG2_brTcKqMJvhGd2h-EBYgA2jWHFIlxeyA=w408-h306-k-no",
    hours: "Mon-Sat: 8:30 AM - 7:00 PM",
    services: ["Product Repairs", "Warranty Claims", "Parts & Accessories"],
  },
  {
    id: 3,
    name: "STIHL XPLOD AGROS",
    type: "Branch",
    category: "kottayam",
    address: "Thengana-Thiruvanchoor Rd, Manarcadu, Kerala",
    lat: 9.589774023174282, 
    lng: 76.5827373312311,
    phone: "+91 9747452539",
    email: "la.distribution@blackanddecker.com",
    image: "https://lh3.googleusercontent.com/gps-cs-s/AC9h4nosPQRjFX7ZoDYa1_SXOujBD4pVLzoSEeY1BsYLrIGnsXA2dd7GpPSmWNNWd66sbIPzPtuXGqkhM6AxWPm1ciDmwQpfaMTSpz-yQyWJV5AU0dinFsiL2y5jG3Ie9rEi9-jHUUFi=w408-h544-k-no",
    hours: "Mon-Sat: 8:30 AM - 7:00 PM",
    services: [
      "Product Distribution",
      "Wholesale Orders",
      "Inventory Management",
    ],
  },
  {
    id: 4,
    name: "STIHL MOTO AGRO",
    type: "Branch",
    category: "pathanamthitta",
    address: "5P4R+PGC, Adoor, Kerala",
    mapUrl: "https://maps.app.goo.gl/aTNMkWEHE14K7F8A6",
    phone: "+1 (312) 555-0177",
    email: "chicago@blackanddecker.com",
    image: "https://www.google.com/maps/place/Stihl+Moto+Agro+Power+Tools/@9.1565609,76.7413503,3a,75y,90t/data=!3m8!1e2!3m6!1sCIHM0ogKEICAgIDxmseexwE!2e10!3e12!6shttps:%2F%2Flh3.googleusercontent.com%2Fgps-cs-s%2FAC9h4nrmTloFWBiuWk7wB_IO3XQssJlgaJcRsh1TzgUlwXrM-tgeZlIZkmDtPDfrkYQnqnaTuuMePGeGvgH-j--gXBgl60Bu8bK15D9keGj5AKptis2KEQprO_9rLzKLkNK47uuiTgzJ2g%3Dw114-h86-k-no!7i4032!8i3024!4m7!3m6!1s0x3b06134b7782e307:0x8960b31c0124411a!8m2!3d9.1566013!4d76.7412125!10e5!16s%2Fg%2F11kq14045h?authuser=0&entry=ttu&g_ep=EgoyMDI1MDkyOS4wIKXMDSoASAFQAw%3D%3D#",
    hours: "Mon-Sat: 8:30 AM - 6:30 PM",
    services: ["Sales Support", "Training Programs", "Technical Support"],
  },
  {
    id: 5,
    name: "STIHL MACHINE WORLD",
    type: "Branch",
    category: "kollam",
    address: "VILAKKUDY PANCHAYAT, Kunnicode, Kerala 691508",
    mapUrl: "https://maps.app.goo.gl/W25gYtC3pg7gxYYg6",
    lat: 9.028736387150614,
    lng: 76.85693904623051,
    phone: "+91 8848064407",
    email: "atlanta.service@blackanddecker.com",
    image: "https://lh3.googleusercontent.com/gps-cs-s/AC9h4noYK7seY5aEksiyVoQRZGXcKB6r4P3xNA3Oed89rlBSnaZplqo5CDsiOIY5Daz4w6J8ThchYGeqWXsPCoDvuMYQOygIyedN6atfSk07-2s55o0vPyhsS2DIIcfGC-EZe6itsmbU=w408-h306-k-no",
    hours: "Mon-Sat: 8:30 AM - 6:30 PM",
    services: ["Product Repairs", "Warranty Service", "Technical Consultation"],
  },
  {
    id: 6,
    name: "STIHL AGRITECH ARYANAD",
    type: "Branch",
    category: "trivandrum",
    address: "Parthasaradhi, Agritech, Building Tholoor, PO, Cherukulam, Aryanad, Kerala",
    lat: 8.5816154484634, 
    lng: 77.08112080971321,
    phone: "+91 9747459993",
    email: "dallas@blackanddecker.com",
    hours: "Mon-Sat: 8:30 AM - 7:00 PM",
    services: ["Regional Sales", "Customer Support", "Training Center"],
  },
];

const categories = [
  { key: "all", label: "All Locations" },
  { key: "trivandrum", label: "Trivandrum" },
  { key: "kollam", label: "Kollam" },
  { key: "pathanamthitta", label: "Pathanamthitta" },
  { key: "alappuzha", label: "Alappuzha" },
  { key: "kottayam", label: "Kottayam" },
];

function BranchesPage() {
  const [activeFilter, setActiveFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid"); // Add this line
  const mapRef = useRef(null);

  useEffect(() => {
    // Load Leaflet only once when component mounts
    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.7.1/dist/leaflet.js";
    script.onload = () => initializeMap();
    document.head.appendChild(script);

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.7.1/dist/leaflet.css";
    document.head.appendChild(link);

    return () => {
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
      if (document.head.contains(link)) {
        document.head.removeChild(link);
      }
    };
  }, []);

  const initializeMap = () => {
    if (typeof window.L === "undefined") return;

    const mapContainer = document.getElementById("branches-map");
    if (!mapContainer) return;

    // Remove existing map if any
    if (mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }

    // Clear container
    mapContainer.innerHTML = "";

    // Initialize map - centered on Kerala
    const map = window.L.map("branches-map").setView([9.5, 76.5], 8);
    mapRef.current = map;

    // Use OpenStreetMap tiles
    window.L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: "© OpenStreetMap contributors",
      maxZoom: 19,
    }).addTo(map);

    // Create custom orange marker icon
    const orangeIcon = window.L.icon({
      iconUrl:
        "data:image/svg+xml;base64," +
        btoa(`
        <svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg">
          <path d="M12.5 0C5.596 0 0 5.596 0 12.5c0 12.5 12.5 28.5 12.5 28.5s12.5-16 12.5-28.5C25 5.596 19.404 0 12.5 0z" fill="#ff6720" stroke="#ffffff" stroke-width="2"/>
          <circle cx="12.5" cy="12.5" r="6" fill="#ffffff"/>
          <circle cx="12.5" cy="12.5" r="3" fill="#ff6720"/>
        </svg>
      `),
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
    });

    // Filter branches based on active filter
    const branchesToShow = activeFilter === "all" 
      ? branches.filter(b => b.lat && b.lng)
      : branches.filter(b => b.category === activeFilter && b.lat && b.lng);

    // Store original bounds
    const allMarkers = branchesToShow.map((branch) => 
      window.L.marker([branch.lat, branch.lng])
    );
    
    if (allMarkers.length > 0) {
      const group = new window.L.featureGroup(allMarkers);
      const originalBounds = group.getBounds().pad(0.1);

      // Add markers for each branch
      branchesToShow.forEach((branch) => {
        const marker = window.L.marker([branch.lat, branch.lng], {
          icon: orangeIcon,
        }).addTo(map);

        // Create popup
        const popup = window.L.popup({
          maxWidth: 320,
          className: "custom-popup",
        }).setContent(`
          <div style="min-width: 280px;">
            <div style="display: flex; gap: 12px; margin-bottom: 12px;">
              <div style="width: 100px; height: 80px; flex-shrink: 0; border-radius: 8px; overflow: hidden; background: #f5f5f5;">
                <img src="${branch.image}"
                    alt="${branch.name}"
                    style="width: 100%; height: 100%; object-fit: cover;"
                    onerror="this.src='https://images.unsplash.com/photo-1441986300917-64674bd600d8?w=400&h=300&fit=crop'"/>
              </div>
              <div style="flex: 1; display: flex; flex-direction: column; justify-content: center;">
                <span style="background-color: #ff6720; color: white; padding: 2px 8px; border-radius: 12px; font-size: 10px; font-weight: 600; margin-bottom: 4px; display: inline-block; width: fit-content;">${branch.type}</span>
                <h3 style="margin: 0 0 6px 0; font-size: 15px; color: #333; font-weight: 600; line-height: 1.3;">${branch.name}</h3>
                <p style="margin: 0 0 4px 0; font-size: 12px; color: #666; line-height: 1.4;">${branch.address}</p>
                <a href="tel:${branch.phone}"
                  style="margin: 0; font-size: 13px; color: #ff6720; font-weight: 600; text-decoration: none; display: flex; align-items: center; gap: 6px; width: fit-content;">
                  📞 ${branch.phone}
                </a>
              </div>
            </div>
            <div style="display: flex; gap: 8px;">
              <button
                onclick="window.location.href='tel:${branch.phone}'"
                style="flex: 1; background: #ff6720; color: white; border: none; padding: 10px 16px; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer;"
                onmouseover="this.style.background='#e55a1c'"
                onmouseout="this.style.background='#ff6720'">
                Call Now
              </button>
              <button
                onclick="window.open('${branch.mapUrl}', '_blank')"
                style="flex: 1; background: transparent; color: #ff6720; border: 1px solid #ff6720; padding: 10px 16px; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer;"
                onmouseover="this.style.background='#ff6720'; this.style.color='white'"
                onmouseout="this.style.background='transparent'; this.style.color='#ff6720'">
                Get Directions
              </button>
            </div>
          </div>
        `);

        marker.bindPopup(popup);

        // Zoom on marker click
        marker.on("click", function () {
          map.setView([branch.lat, branch.lng], 14, {
            animate: true,
            duration: 1,
          });
        });

        // Reset view when popup closes
        popup.on("remove", function () {
          map.fitBounds(originalBounds, {
            animate: true,
            duration: 1,
          });
        });
      });

      // Fit map to show all markers
      map.fitBounds(originalBounds);
    }

    // Force map to refresh its tiles
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 200);
  };

  useEffect(() => {
    if (viewMode === "map") {
      // Load Leaflet when switching to map view
      const script = document.createElement("script");
      script.src = "https://unpkg.com/leaflet@1.7.1/dist/leaflet.js";
      script.onload = () => {
        setTimeout(() => initializeMap(), 100);
      };
      
      if (!document.querySelector('script[src="https://unpkg.com/leaflet@1.7.1/dist/leaflet.js"]')) {
        document.head.appendChild(script);
      } else if (typeof window.L !== "undefined") {
        setTimeout(() => initializeMap(), 100);
      }

      const link = document.createElement("link");
      link.rel = "stylesheet";
      link.href = "https://unpkg.com/leaflet@1.7.1/dist/leaflet.css";
      
      if (!document.querySelector('link[href="https://unpkg.com/leaflet@1.7.1/dist/leaflet.css"]')) {
        document.head.appendChild(link);
      }
    }
  }, [viewMode]);

  useEffect(() => {
    document.title = "Our Branches | XPLOD-GROUPS";
    return () => {
      document.title = "Home | XPLOD-GROUPS";
    };
  }, []);

  const filteredBranches =
    activeFilter === "all"
      ? branches
      : branches.filter((branch) => branch.category === activeFilter);

  const handleGetDirections = (mapUrl) => {
    window.open(mapUrl, "_blank");
  };

  const handleFilterChange = (filter) => {
    setActiveFilter(filter);
    if (viewMode === "map" && mapRef.current) {
      setTimeout(() => initializeMap(), 100);
    }
  };

  const handleContact = (phone) => {
    window.open(`tel:${phone}`, "_self");
  };

  return (
    <BranchesContainer>
      <HeroSection>
        <div className="container" style={{maxWidth: "1300px"}}>
          <HeroTitle>Our Locations</HeroTitle>
          <div style={{width: "80px", backgroundColor: "white", height: "10px", marginTop: "-10px"}}/>
          <HeroSubtitle>
            Find a STIHL XPLOD-GROUPS location near you for product support, services,
            and expert assistance with all your machine needs.
          </HeroSubtitle>
        </div>
      </HeroSection>

      <BranchesSection>
        <div className="container" style={{maxWidth: "1300px"}}>
          <FilterAndViewContainer>
            {/* Location Filter Dropdown - Only show in List View */}
            {viewMode === "grid" && (
              <LocationFilterContainer>
                <LocationFilterLabel>Location:</LocationFilterLabel>
                <LocationFilterSelect
                  value={activeFilter}
                  onChange={(e) => handleFilterChange(e.target.value)}
                >
                  {categories.map((category) => (
                    <option key={category.key} value={category.key}>
                      {category.label}
                    </option>
                  ))}
                </LocationFilterSelect>
              </LocationFilterContainer>
            )}

            {/* View Toggle Buttons */}
            <ViewToggleButtons>
              <ViewButton 
                active={viewMode === "grid"} 
                onClick={() => setViewMode("grid")}
              >
                <FaList />
                List View
              </ViewButton>
              <ViewButton 
                active={viewMode === "map"} 
                onClick={() => {
                  setViewMode("map");
                  setActiveFilter("all"); // Reset filter when switching to map view
                }}
              >
                <FaMap />
                Map View
              </ViewButton>
            </ViewToggleButtons>
          </FilterAndViewContainer>

          {/* Grid View Section */}
          <BranchGridSection show={viewMode === "grid"}>
            <BranchGrid>
              {filteredBranches.map((branch) => (
                <BranchCard key={branch.id}>
                  <BranchImage $bgImage={branch.image} />
                  <BranchContent>
                    <BranchName>{branch.name}</BranchName>
                    <BranchType>{branch.type}</BranchType>

                    <BranchInfo>
                      <InfoItem>
                        <BnIcon>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M.505 2.354A1 1 0 0 0 0 3.226v9.273a1 1 0 0 0 1.508.864l2.63-1.72a.25.25 0 0 0 .113-.21V.263A.25.25 0 0 0 3.86.057zM5.636.04a.25.25 0 0 0-.386.21v11.173a.25.25 0 0 0 .113.21l3 1.95a.25.25 0 0 0 .387-.209V2.208a.25.25 0 0 0-.114-.21zm7.88.224A.99.99 0 0 0 12.494.26l-2.63 1.719a.25.25 0 0 0-.113.21v11.17a.25.25 0 0 0 .391.207l3.346-2.295a1 1 0 0 0 .513-.875v-9.25a1.02 1.02 0 0 0-.483-.882" fill="#666"/></svg>
                        </BnIcon>
                        <span>{branch.address}</span>
                      </InfoItem>
                      <InfoItem>
                        <BnIcon>
                        <FaPhone />
                        </BnIcon>
                        <span>{branch.phone}</span>
                      </InfoItem>
                      <InfoItem>
                        <BnIcon>
                        <IoIosMail />
                        </BnIcon>
                        <span>{branch.email}</span>
                      </InfoItem>
                      <InfoItem>
                        <BnIcon>
                        <IoTime />
                        </BnIcon>
                        <span>{branch.hours}</span>
                      </InfoItem>
                    </BranchInfo>

                    <BranchActions>
                      <ActionButton
                        className="primary"
                        onClick={() => handleGetDirections(branch.mapUrl)}
                      >
                        Get Directions
                      </ActionButton>
                      <ActionButton
                        className="secondary"
                        onClick={() => handleContact(branch.phone)}
                      >
                        Call Now
                      </ActionButton>
                    </BranchActions>
                  </BranchContent>
                </BranchCard>
              ))}
            </BranchGrid>
          </BranchGridSection>

          {/* Map View Section */}
          <MapSection show={viewMode === "map"}>
            <MapContainer>
              <MapWrapper>
                <div id="branches-map" />
              </MapWrapper>
            </MapContainer>
          </MapSection>
        </div>
      </BranchesSection>
    </BranchesContainer>
  );
}

export default BranchesPage;
